import React from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useMixpanel } from "react-mixpanel-browser";

import useAuthState from "../../hooks/useAuthState";
import pathToPageName from "../../helpers/pathToPageName";
import { Login } from "../../pages/Login";
import { InfluencerLogin } from "../../pages/InfluencerLogin";
import { InfluencerRegistration } from "../../pages/InfluencerRegistration";
import {
  OnboardingProfileInfo,
  OnboardingOrganizationInfo,
  OnboardingPaymentInfo
} from "../../pages/Onboarding";
import { ForgotPassword } from "../../pages/ForgotPassword";
import ProtectedRoute from "./ProtectedRoute";
import { Products } from "../../pages/Products";
import { ChatPanel } from "../../pages/ChatPanel";
import { Orders } from "../../pages/Orders";
import { Reporting } from "../../pages/Reporting";
import { OrderCashOut } from "../../pages/OrderCashOut";
import { Opportunities } from "../../pages/Opportunities";
import { OpportunityDetails } from "../../pages/OpportunityDetails";
import { Videos } from "../../pages/Videos";
import { OpportunityLink } from "../../pages/OpportunityLink";
import { Settings } from "../../pages/Settings";
import { ConfirmationEmail } from "../../pages/OrderConfirmationEmail";
import { Dashboard } from "../../pages/Dashboard";
import { Brand } from "../../pages/Brand";
import useTutorialsState from "../../hooks/useTutorialsState";

function Routes() {
  const { isLoggedIn, user } = useAuthState();
  const mixpanel = useMixpanel();
  const { isNmiAccount } = useTutorialsState();

  const bootIntercom = () => {
    if (isLoggedIn) {
      window.Intercom("boot", {
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        user_id: user.sub,
        email: user.email,
        name: user.name
      });
    } else {
      window.Intercom("boot", {
        app_id: process.env.REACT_APP_INTERCOM_APP_ID
      });
    }
    window.Intercom("show");
  };

  const trackPageVisitOnMixpanel = (routeProps) => {
    const PageName = pathToPageName[routeProps.match.path];
    mixpanel.track("Page Visited", {
      ...(PageName && { PageName }),
      Path: routeProps.match.patch,
      Parameters: routeProps.match.params
    });
  };

  return (
    <Switch>
      <Redirect exact from="/" to="/dashboard" />
      <ProtectedRoute
        path="/products/:id/confirmation-email"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={ConfirmationEmail}
      />
      <ProtectedRoute
        path="/products"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Products}
      />
      <ProtectedRoute
        path="/opportunities/:id"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={OpportunityDetails}
      />
      <ProtectedRoute
        path={"/opportunities"}
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Opportunities}
      />
      <ProtectedRoute
        path="/orders"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Orders}
      />
      <ProtectedRoute
        path="/chat"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={ChatPanel}
      />
      <ProtectedRoute
        path="/opportunities"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Opportunities}
      />
      <ProtectedRoute
        path="/videos"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Videos}
      />
      <ProtectedRoute
        path={[
          "/settings/:tabId/:resourceId/zone/:zoneId",
          "/settings/:tabId/:resourceId",
          "/settings/:tabId"
        ]}
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Settings}
      />
      <ProtectedRoute
        path={[
          "/reporting/product/:productId",
          "/reporting/video/:videoId",
          "/reporting"
        ]}
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Reporting}
      />
      {!isNmiAccount && (
        <ProtectedRoute
          path="/cashout"
          bootIntercom={bootIntercom}
          trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
          component={OrderCashOut}
        />
      )}
      <Route
        path="/login"
        render={(routeProps) => {
          if (isLoggedIn) return <Redirect to="/" />;
          return <Login {...routeProps} />;
        }}
      />
      <Route
        path={["/register/:step", "/register"]}
        render={(routeProps) => {
          bootIntercom();
          trackPageVisitOnMixpanel(routeProps);
          window.Intercom("update");
          if (!isLoggedIn && routeProps.match.params.step)
            return <Redirect to="/register" />;

          switch (routeProps.match.params.step) {
            case "profile":
              return <OnboardingProfileInfo {...routeProps} />;

            case "organization":
              return <OnboardingOrganizationInfo {...routeProps} />;

            case "payment":
              return <OnboardingPaymentInfo {...routeProps} />;

            default:
              return <OnboardingProfileInfo {...routeProps} />;
          }
        }}
      />
      <Route
        path="/forgotPassword"
        render={(routeProps) => {
          bootIntercom();
          trackPageVisitOnMixpanel(routeProps);
          window.Intercom("update");
          return <ForgotPassword {...routeProps} />;
        }}
      />
      <Route
        path="/share/opportunity/:opportunityId"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={OpportunityLink}
      />
      <Route
        path="/influencer/login"
        render={(routeProps) => {
          if (isLoggedIn) return <Redirect to="/" />;
          return <InfluencerLogin {...routeProps} />;
        }}
      />
      <Route
        path="/influencer/register"
        render={(routeProps) => {
          bootIntercom();
          trackPageVisitOnMixpanel(routeProps);
          window.Intercom("update");
          return <InfluencerRegistration {...routeProps} />;
        }}
      />
      <ProtectedRoute
        path="/dashboard"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Dashboard}
      />
      <Route
        path="/brand/:username"
        bootIntercom={bootIntercom}
        trackPageVisitOnMixpanel={trackPageVisitOnMixpanel}
        component={Brand}
      />
    </Switch>
  );
}

export default Routes;
